import "./SectionSidebarOptionsMenu.scss";

import { FC, Ref, useRef, useState } from "react";

import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import { CourseCodeMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { CancelSaveWidget } from "mds/components/CancelSaveWidget";
import { IconText } from "mds/components/IconText";
import { MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import {
  ArrowDownIcon,
  CopySendArrowIcon,
  EyeNotVisibleIcon,
  EyeVisibleIcon,
  GearSettingsIcon,
} from "mds/icons";
import { useAppSelector } from "store/index";
import { selectTermById } from "store/selectors/section";

interface CourseSidebarOptionsMenuProps extends SidebarOptionsMenuProps {
  course: SectionType;
  optionsMenuActions: SidebarOptionsMenuProps["optionsMenuActions"] & {
    onSettings?: () => void;
    onCopyTo?: (courseId: string, courseCodeId?: string, termId?: string) => void;
    onMoveTo?: (courseCodeId: string, termId: string) => void;
    onExport?: () => void;
    onArchive?: () => void;
    onUnarchive?: () => void;
  };
}

export const CourseSidebarOptionsMenu: FC<CourseSidebarOptionsMenuProps> = ({
  styles,
  size = "s",
  className,
  direction = "right",
  course,
  optionsMenuActions,
}) => {
  const courseSidebarMenuRef: Ref<MenuInstance> = useRef(null);
  const [selectedCourseCodeId, setSelectedCourseCodeId] = useState(course.course_id);
  const [selectedTermId, setSelectedTermId] = useState(course.term_id);
  const term = useAppSelector((s) => selectTermById(s, course.term_id));
  const termDisabled = term?.archived_at !== null;

  // NOTE: We use the same state for both copy and move to, this should be fine as these menus will never
  // render at the same time
  const handleCourseCodeChange = (newCourseCodeId: string) => {
    setSelectedCourseCodeId(newCourseCodeId);
  };

  const handleTermChange = (newTermId: string) => {
    setSelectedTermId(newTermId);
  };

  const copyToMenuItem = optionsMenuActions.onCopyTo && (
    <SubMenu
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.copy_to")} />
      }
      withArrow
    >
      <div className="copy-course-menu h4 m-2 min-w-[280px]">
        <div className="pb-2 text-black-tint-40">{t("common.copy_to")}</div>

        <div className="flex">
          <CourseCodeMenu
            defaultToAll={false}
            selectedCourseCodeId={selectedCourseCodeId}
            onCourseCodeChange={handleCourseCodeChange}
          />

          <TermMenu
            defaultToAll={false}
            selectedTermId={selectedTermId}
            onTermChange={handleTermChange}
          />
        </div>

        <CancelSaveWidget
          className="right pt-2"
          saveTextKey="copy"
          onClickCancel={() => {
            courseSidebarMenuRef.current.closeMenu();
          }}
          onClickSave={() =>
            optionsMenuActions.onCopyTo(course.id, selectedCourseCodeId, selectedTermId)
          }
        />
      </div>
    </SubMenu>
  );

  const moveToMenuItem = optionsMenuActions.onMoveTo && (
    <SubMenu
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_course")} />
      }
      withArrow
    >
      <div className="copy-course-menu h4 m-2 min-w-[280px]">
        <div className="pb-2 text-black-tint-40">{t("sidebar_options_menu.move_course_to")}</div>

        <div className="flex">
          <CourseCodeMenu
            defaultToAll={false}
            selectedCourseCodeId={selectedCourseCodeId}
            onCourseCodeChange={handleCourseCodeChange}
          />

          <TermMenu
            defaultToAll={false}
            selectedTermId={selectedTermId}
            onTermChange={handleTermChange}
          />
        </div>

        <CancelSaveWidget
          className="right pt-2"
          saveTextKey="move"
          onClickCancel={() => {
            courseSidebarMenuRef.current.closeMenu();
          }}
          onClickSave={() => {
            optionsMenuActions.onMoveTo(selectedCourseCodeId, selectedTermId);
            courseSidebarMenuRef.current.closeMenu();
          }}
        />
      </div>
    </SubMenu>
  );

  const courseSettingItem = optionsMenuActions.onSettings && (
    <MenuItem
      eventName="Admin Courses - Course Settings - Open"
      title={t("settings.course_settings")}
      onClick={optionsMenuActions.onSettings}
    >
      <IconText iconStart={<GearSettingsIcon />} text={t("settings.course_settings")} />
    </MenuItem>
  );

  const exportMenuItem = optionsMenuActions.onExport && (
    <MenuItem onClick={optionsMenuActions.onExport}>
      <IconText iconStart={<ArrowDownIcon />} text={t("common.export")} />
    </MenuItem>
  );

  const archiveMenuItem = !termDisabled && !course.archived_at && optionsMenuActions.onArchive && (
    <MenuItem onClick={optionsMenuActions.onArchive}>
      <IconText iconStart={<EyeNotVisibleIcon />} text={t("sidebar_options_menu.archive")} />
    </MenuItem>
  );

  const unarchiveMenuItem = (termDisabled || course.archived_at) &&
    optionsMenuActions.onUnarchive && (
      <MenuItem disabled={termDisabled} onClick={optionsMenuActions.onUnarchive}>
        <IconText iconStart={<EyeVisibleIcon />} text={t("sidebar_options_menu.unarchive")} />
      </MenuItem>
    );

  return (
    <SidebarOptionsMenu
      className={className}
      direction={direction}
      instanceRef={courseSidebarMenuRef}
      optionsMenuActions={optionsMenuActions}
      size={size}
      styles={styles}
    >
      {courseSettingItem}
      {copyToMenuItem}
      {moveToMenuItem}
      {exportMenuItem}
      {archiveMenuItem}
      {unarchiveMenuItem}
    </SidebarOptionsMenu>
  );
};
